import { del, get, post, put } from '../utils/networking';
import type { TemplateLanguage, TranslationEntry } from './hms';

export type ChecklistStatus = 'published' | 'draft' | 'archived';

export type ChecklistVersion = {
  version: number;
  publishedAt: string | null;
  changeNote: string | null;
  publishedByEmail: string | null;
};

/** A template as listed in «Sjekklistemaler» */
export type ChecklistCard = {
  id: number;
  source: 'STANDARD' | 'CUSTOM';
  name: string;
  description: string;
  categories: string[];
  status: ChecklistStatus;
  /** The version in use, or null before the first is published */
  version: number | null;
  hasDraft: boolean;
  checkpoints: number;
  required: number;
  activePlans: number;
  newSuggestions: number;
  versions: ChecklistVersion[];
};

export type EditableCheckpoint = {
  key: string;
  number: string;
  text: string;
  help: string | null;
  required: boolean;
};

export type EditableCategory = { key: string; name: string; checkpoints: EditableCheckpoint[] };

export type ChecklistTranslationKind = 'templateName' | 'templateDescription' | 'category' | 'checkpoint' | 'help';

export type ChecklistTranslationItem = {
  key: string;
  kind: ChecklistTranslationKind;
  categoryKey: string | null;
  categoryName: string | null;
  number: string | null;
  source: string;
  text: string;
  aiDraft: boolean;
  standard: string | null;
  translated: boolean;
};

export type ChecklistLanguage = TemplateLanguage & {
  direction: 'ltr' | 'rtl';
  translated: number;
  total: number;
  items: ChecklistTranslationItem[];
};

export type ChecklistSuggestion = {
  id: number;
  text: string;
  categoryKey: string | null;
  categoryName: string | null;
  fromEmail: string | null;
  status: 'NEW' | 'ACCEPTED' | 'REJECTED';
  comment: string | null;
  createdAt: string;
};

export type ChecklistEditor = ChecklistCard & {
  nextVersion: number;
  editing: { name: string; description: string; categories: EditableCategory[] };
  languages: ChecklistLanguage[];
  selectableLanguages: string[];
  suggestions: ChecklistSuggestion[];
};

/** New categories and checkpoints have no key yet */
export type ChecklistDraftInput = {
  name: string;
  description: string;
  categories: Array<{
    key?: string;
    name: string;
    checkpoints: Array<{ key?: string; text: string; help?: string | null; required: boolean }>;
  }>;
};

const BASE = '/api/safety-rounds/templates';

export const getChecklists = () => get<ChecklistCard[]>(BASE);
export const getPublishedChecklists = () => get<ChecklistCard[]>(`${BASE}/published`);
export const getChecklist = (id: number) => get<ChecklistEditor>(`${BASE}/${id}`);
export const createChecklist = (input: { name: string; description: string }) =>
  post<{ id: number }>(BASE, input);
export const saveChecklistDraft = (id: number, draft: ChecklistDraftInput) =>
  put<ChecklistEditor>(`${BASE}/${id}/draft`, draft);
export const discardChecklistDraft = (id: number) => del<ChecklistEditor>(`${BASE}/${id}/draft`);
export const saveChecklistLanguages = (id: number, languages: TemplateLanguage[]) =>
  put<ChecklistEditor>(`${BASE}/${id}/draft/languages`, { languages });
export const saveChecklistTranslations = (id: number, language: string, entries: TranslationEntry[]) =>
  put<ChecklistEditor>(`${BASE}/${id}/draft/translations/${language}`, { entries });
export const suggestChecklistTranslations = (id: number, language: string) =>
  post<ChecklistEditor>(`${BASE}/${id}/draft/translations/${language}/suggestions`);
export const publishChecklist = (id: number, input: { changeNote: string; notifySafetyReps: boolean }) =>
  post<ChecklistEditor>(`${BASE}/${id}/publish`, input);
export const setChecklistArchived = (id: number, archived: boolean) =>
  put<ChecklistEditor>(`${BASE}/${id}/archived`, { archived });
export const suggestCheckpoint = (id: number, input: { categoryKey: string | null; text: string }) =>
  post<{ id: number }>(`${BASE}/${id}/suggestions`, input);
export const acceptSuggestion = (suggestionId: number) =>
  post<ChecklistEditor>(`${BASE}/suggestions/${suggestionId}/accept`);
export const rejectSuggestion = (suggestionId: number, comment: string) =>
  post<ChecklistEditor>(`${BASE}/suggestions/${suggestionId}/reject`, { comment });

export type Frequency = 'ANNUAL' | 'SEMIANNUAL' | 'QUARTERLY' | 'MONTHLY';
export type ParticipantRole = 'GENERAL_MANAGER' | 'SAFETY_REP' | 'LEADER' | 'OCCUPATIONAL_HEALTH' | 'EMPLOYEE';
/**
 * A plan whose protocol waits for the general manager is walked, so it is neither due nor overdue. One the general
 * manager sent back is the safety representative's to correct.
 */
export type PlanStatus = 'onTrack' | 'soon' | 'overdue' | 'awaitingSignature' | 'sentBack';

export type PlanCard = {
  id: number;
  name: string;
  area: string;
  frequency: Frequency;
  nextDate: string;
  noticeDays: number;
  lastCompletedOn: string | null;
  participants: ParticipantRole[];
  active: boolean;
  status: PlanStatus;
  safetyRepEmail: string | null;
  template: { name: string; version: number } | null;
  openDeviations: number;
  currentRoundId: number | null;
  /** The round can be started from its notice period */
  canStart: boolean;
  startsFrom: string;
  mayEdit: boolean;
  mayConduct: boolean;
};

export type WalkedRound = {
  id: number;
  planId: number;
  planName: string;
  status: 'AWAITING_SIGNATURE' | 'SIGNED';
  plannedDate: string;
  completedOn: string | null;
  deviations: number;
};

export type PlansOverview = { today: string; plans: PlanCard[]; archived: PlanCard[]; rounds: WalkedRound[] };

export type PlanOptions = {
  templates: Array<{ id: number; name: string; description: string; version: number; checkpoints: number }>;
  safetyReps: Array<{ userId: number; email: string }>;
  choosesSafetyRep: boolean;
  /** The only one who can sign for the employer. Missing from older backends. */
  onlyGeneralManagerId?: number | null;
  participantRoles: ParticipantRole[];
  lockedParticipants: ParticipantRole[];
  noticeDays: number[];
};

export type CustomizedCheckpoint = {
  key: string | null;
  extraId: number | null;
  number: string;
  text: string;
  help: string | null;
  required: boolean;
  hidden: boolean;
  source: 'TEMPLATE' | 'PLAN';
};

export type CustomizedCategory = { key: string; number: number; name: string; checkpoints: CustomizedCheckpoint[] };

export type PlanDetail = Omit<PlanCard, 'template' | 'openDeviations' | 'currentRoundId' | 'safetyRepEmail' | 'mayConduct'> & {
  templateId: number;
  safetyRepId: number;
  safetyRepEmail: string | null;
  /** The safety representative has the SAFETY_REP role, so they can walk and sign the round. Missing from older backends. */
  safetyRepHasRole?: boolean;
  template: { name: string; version: number; archived: boolean };
  checklist: CustomizedCategory[];
  checkpoints: number;
  hiddenCount: number;
  extraCount: number;
  mayCustomize: boolean;
};

export type PlanInput = {
  name: string;
  area: string;
  templateId: number | null;
  frequency: Frequency;
  nextDate: string;
  noticeDays: number;
  participants: ParticipantRole[];
  safetyRepId?: number | null;
};

export type CustomizationInput = {
  hiddenKeys: string[];
  extra: Array<{ categoryKey: string; text: string }>;
};

const PLANS = '/api/safety-rounds/plans';

export const getPlans = (language: string) => get<PlansOverview>(PLANS, { params: { language } });
export const getPlanOptions = (language: string) => get<PlanOptions>(`${PLANS}/options`, { params: { language } });
export const getPlan = (id: number, language: string) => get<PlanDetail>(`${PLANS}/${id}`, { params: { language } });
export const createPlan = (input: PlanInput) => post<{ id: number }>(PLANS, input);
export const updatePlan = (id: number, input: PlanInput) => put<{ id: number }>(`${PLANS}/${id}`, input);
export const setPlanActive = (id: number, active: boolean) => put<{ id: number }>(`${PLANS}/${id}/active`, { active });
export const savePlanCustomization = (id: number, input: CustomizationInput, language: string) =>
  put<PlanDetail>(`${PLANS}/${id}/customization`, input, { params: { language } });

export type RoundResult = 'OK' | 'DEVIATION' | 'NOT_CHECKED' | 'NOT_RELEVANT';

export type RoundAnswer = {
  result: RoundResult;
  comment: string | null;
  description: string | null;
  responsibleId: number | null;
  dueDate: string | null;
};

export type RoundItem = {
  id: number;
  number: string;
  text: string;
  help: string | null;
  required: boolean;
  source: 'TEMPLATE' | 'PLAN' | 'ROUND';
  answer: RoundAnswer | null;
};

export type RoundStatus = 'PLANNED' | 'IN_PROGRESS' | 'AWAITING_SIGNATURE' | 'SIGNED';

/** A checkpoint that still lacks something before the safety representative can sign */
export type RoundProblemRef = { type: 'unanswered' | 'notCheckedReason' | 'deviationDescription'; itemId: number; number: string };

/** What the general manager asked to have corrected when sending the protocol back */
export type SentBackNote = { comment: string; byEmail: string | null; createdAt: string };

export type RoundView = {
  id: number;
  status: RoundStatus;
  plannedDate: string;
  startedAt: string | null;
  lastSavedAt: string | null;
  templateVersion: number | null;
  plan: { id: number; name: string; area: string };
  participants: ParticipantRole[];
  categories: Array<{ key: string; number: number; name: string; items: RoundItem[] }>;
  progress: { done: number; total: number; deviations: number; notChecked: number };
  problems: RoundProblemRef[];
  mayConduct: boolean;
  editable: boolean;
  people: Array<{ userId: number; email: string }>;
  /** The reader is the plan's safety representative, who walks the round and signs first. Missing from older backends. */
  readerIsSafetyRep?: boolean;
  /** The plan's safety representative, who signs first even when the general manager walked the round */
  safetyRepEmail?: string | null;
  /** While the safety representative corrects a protocol sent back. Missing from older backends. */
  sentBack?: SentBackNote | null;
};

export type AnswerInput = {
  result: RoundResult | null;
  comment?: string | null;
  description?: string | null;
  responsibleId?: number | null;
  dueDate?: string | null;
};

export const startRound = (planId: number) => post<{ id: number }>(`/api/safety-rounds/plans/${planId}/rounds`);
export const getRound = (id: number, language: string) =>
  get<RoundView>(`/api/safety-rounds/rounds/${id}`, { params: { language } });
export const saveRoundAnswer = (roundId: number, itemId: number, input: AnswerInput) =>
  put<{ savedAt: string }>(`/api/safety-rounds/rounds/${roundId}/answers/${itemId}`, input);
export const addRoundItem = (roundId: number, input: { categoryKey: string; text: string }, language: string) =>
  post<RoundView>(`/api/safety-rounds/rounds/${roundId}/items`, input, { params: { language } });
export const removeRoundItem = (roundId: number, itemId: number, language: string) =>
  del<RoundView>(`/api/safety-rounds/rounds/${roundId}/items/${itemId}`, { params: { language } });

export type DeviationStatus = 'OPEN' | 'FIXED' | 'AWAITING_DECISION' | 'CLOSED' | 'ACCEPTED';

export type Protocol = {
  language: string;
  planName: string;
  area: string;
  templateName: string;
  templateVersion: number | null;
  plannedDate: string;
  completedOn: string | null;
  participants: ParticipantRole[];
  counts: { total: number; ok: number; deviations: number; notChecked: number; notRelevant: number };
  deviations: Array<{
    itemId: number;
    deviationId: number | null;
    number: string;
    text: string;
    description: string;
    responsibleEmail: string | null;
    dueDate: string | null;
    status: DeviationStatus | null;
  }>;
  notChecked: Array<{ number: string; text: string; reason: string | null }>;
  comments: Array<{ number: string; text: string; comment: string }>;
  categories: Array<{
    number: number;
    name: string;
    items: Array<{ itemId: number; number: string; text: string; result: RoundResult | null; comment: string | null }>;
  }>;
  signatures: Array<{ role: 'SAFETY_REP' | 'GENERAL_MANAGER'; email: string | null; signedAt: string }>;
  changeRequests: Array<{ comment: string; byEmail: string | null; createdAt: string }>;
};

export type ProtocolView = {
  roundId: number;
  planId: number;
  status: RoundStatus;
  locked: boolean;
  protocol: Protocol;
  languages: string[];
  problems: RoundProblemRef[];
  maySignAsSafetyRep: boolean;
  maySignAsGeneralManager: boolean;
  mayEditRound: boolean;
  /** The safety representative of the plan, who signs first */
  safetyRepEmail: string | null;
  /** The plan's safety representative does not have the role, so no one can sign as safety representative yet */
  safetyRepWithoutRole?: boolean;
  /** The general manager who signed, or the only one who can */
  generalManagerEmail?: string | null;
  /** The reader signs, or signed, as safety representative */
  readerIsSafetyRep?: boolean;
  /** The reader signed for the employer, or is the only general manager who can */
  readerIsGeneralManager?: boolean;
  /** No one has the role of general manager, so no one can sign for the employer yet */
  generalManagerMissing: boolean;
  /** The only general manager is the one who signs as safety representative, and cannot sign both */
  generalManagerIsSafetyRep?: boolean;
  /** The reader is a general manager who signed as safety representative, so another one signs for the employer */
  signedAsSafetyRep?: boolean;
  /** The open deviations the general manager may give another responsible or deadline before signing */
  deviationsToChange?: Array<{ deviationId: number; itemId: number; responsibleId: number | null; dueDate: string }>;
  /** Who can be responsible, when the reader may change the deviations */
  people?: Array<{ userId: number; email: string }>;
};

export const getProtocol = (roundId: number, language: string) =>
  get<ProtocolView>(`/api/safety-rounds/rounds/${roundId}/protocol`, { params: { language } });
export const signProtocol = (
  roundId: number,
  input: { role: 'SAFETY_REP' | 'GENERAL_MANAGER'; confirmedRead?: boolean },
  language: string,
) => post<ProtocolView>(`/api/safety-rounds/rounds/${roundId}/signatures`, input, { params: { language } });
export const changeProtocolDeviation = (
  roundId: number,
  deviationId: number,
  input: { responsibleId: number; dueDate: string },
  language: string,
) => put<ProtocolView>(`/api/safety-rounds/rounds/${roundId}/deviations/${deviationId}`, input, { params: { language } });
export const requestProtocolChange = (roundId: number, comment: string, language: string) =>
  post<ProtocolView>(`/api/safety-rounds/rounds/${roundId}/change-request`, { comment }, { params: { language } });

/** The backend makes the PDF, so every checkpoint is included whatever is open on the screen */
export const downloadProtocolPdf = async (roundId: number, language: string): Promise<Blob> => {
  const params = new URLSearchParams({ key: 'safety-round-protocol', roundId: String(roundId), language, download: 'true' });
  const response = await fetch(`/api/pdf-export?${params}`, { method: 'GET', credentials: 'include' });
  if (!response.ok) throw new Error('PDF export failed');
  return await response.blob();
};

export type DeviationAction =
  | 'markFixed'
  | 'moveDeadline'
  | 'cannotFix'
  | 'confirmFixed'
  | 'sendBack'
  | 'acceptRisk'
  | 'mustBeFixed'
  | 'remind';

export type DueState = 'overdue' | 'soon' | 'onTime' | 'done';

export type DeviationView = {
  id: number;
  number: string;
  title: string;
  plan: { id: number; name: string; area: string };
  roundId: number;
  foundOn: string;
  status: DeviationStatus;
  step: 1 | 2 | 3;
  dueDate: string;
  dueState: DueState;
  description: string;
  responsibleEmail: string | null;
  actionTaken: string | null;
  history: Array<{
    type: 'REGISTERED' | 'FIXED' | 'DEADLINE_MOVED' | 'RESPONSIBLE_CHANGED' | 'CANNOT_FIX' | 'CLOSED' | 'SENT_BACK' | 'RISK_ACCEPTED' | 'MUST_BE_FIXED' | 'REMINDED';
    actorEmail: string | null;
    comment: string | null;
    fromDueDate: string | null;
    toDueDate: string | null;
    at: string;
    /** The safety representative confirmed the fix of a deviation they were responsible for. Missing from older backends. */
    ownFix?: boolean;
  }>;
  actions: DeviationAction[];
  isResponsible: boolean;
  /** Closed by the safety representative, who was also responsible for the fix. Missing from older backends. */
  confirmedOwnFix?: boolean;
  /** The general manager has signed the protocol, so the deviation has gone out to the responsible */
  released?: boolean;
};

export type DeviationSummary = {
  id: number;
  number: string;
  title: string;
  planName: string;
  status: DeviationStatus;
  dueDate: string;
  dueState: DueState;
};

export type MyDeviations = { toDo: DeviationSummary[]; waiting: DeviationSummary[]; done: DeviationSummary[] };

export const getDeviation = (id: number, language: string) =>
  get<DeviationView>(`/api/safety-rounds/deviations/${id}`, { params: { language } });
export const getMyDeviations = (language: string) => get<MyDeviations>('/api/safety-rounds/my-deviations', { params: { language } });

export type TaskDeviation = DeviationSummary & { responsibleEmail: string | null };

export type TaskRound = {
  id: number;
  planId: number;
  planName: string;
  status: RoundStatus;
  completedOn: string | null;
  changeRequested: boolean;
  signedAt: string | null;
  /** The deviation cases of the round. Missing from older backends. */
  deviations?: number;
  /** The general manager's latest request for a change, for the safety representative. Missing from older backends. */
  changeRequest?: { comment: string; at: string } | null;
};

/** A deviation the safety representative checks or follows. The extra facts are missing from older backends. */
export type RepTaskDeviation = TaskDeviation & {
  actionTaken?: string | null;
  /** The latest thing that happened to it, such as who marked it as fixed */
  lastEvent?: { type: DeviationView['history'][number]['type']; at: string; actorEmail: string | null } | null;
  /** The safety representative is responsible for it themselves */
  ownFix?: boolean;
};

/** What the safety representative has done in safety rounds the last 90 days */
export type RepCompletedRound =
  | { kind: 'protocol'; id: number; planName: string; status: RoundStatus; at: string }
  | { kind: 'deviation'; id: number; number: string; title: string; planName: string; at: string };

export type RoundToDo = {
  planId: number;
  planName: string;
  area: string;
  nextDate: string;
  status: PlanStatus;
  roundId: number | null;
  /** Missing from older backends */
  frequency?: Frequency | null;
};

/** «Mine oppgaver». Each part is there only for those with the role. */
export type MyTasks = {
  safetyRep: {
    roundsToDo: RoundToDo[];
    protocols: TaskRound[];
    toCheck: RepTaskDeviation[];
    following: RepTaskDeviation[];
    /** Missing from older backends */
    completed?: RepCompletedRound[];
    count: number;
  } | null;
  generalManager: {
    toSign: TaskRound[];
    toDecide: TaskDeviation[];
    responsible: TaskDeviation[];
    signed: TaskRound[];
    count: number;
  } | null;
};

export const getMyTasks = (language: string) => get<MyTasks>('/api/safety-rounds/my-tasks', { params: { language } });

export type NotificationEvent =
  | 'ROUND_APPROACHING'
  | 'ROUND_DAY_BEFORE'
  | 'ROUND_OVERDUE'
  | 'SIGNATURE_WAITING'
  | 'SIGNATURE_OVERDUE'
  | 'PROTOCOL_SENT_BACK'
  | 'DEVIATION_ASSIGNED'
  | 'DEVIATION_DUE_SOON'
  | 'DEVIATION_OVERDUE'
  | 'DEVIATION_FIXED'
  | 'DEVIATION_SENT_BACK'
  | 'DEVIATION_TO_DECISION'
  | 'DEVIATION_REMINDER'
  | 'TEMPLATE_CHANGED';

export type NotificationRole = 'safetyRep' | 'generalManager' | 'hr' | 'responsible';

/** «Mine varsler»: the events that can reach the user, whether they come by email, and the roles they are for */
export type NotificationSettings = {
  events: Array<{ event: NotificationEvent; email: boolean; roles?: NotificationRole[] }>;
};

export const getNotificationSettings = () => get<NotificationSettings>('/api/safety-rounds/notification-settings');
export const saveNotificationSettings = (choices: Array<{ event: NotificationEvent; email: boolean }>) =>
  put<NotificationSettings>('/api/safety-rounds/notification-settings', { choices });
export const actOnDeviation = (
  id: number,
  action: Exclude<DeviationAction, 'remind'>,
  input: { comment?: string; dueDate?: string },
  language: string,
) => post<DeviationView>(`/api/safety-rounds/deviations/${id}/actions/${action}`, input, { params: { language } });
export const remindDeviation = (id: number, language: string) =>
  post<DeviationView>(`/api/safety-rounds/deviations/${id}/reminder`, {}, { params: { language } });

export type OversightPlan = {
  id: number;
  name: string;
  area: string;
  nextDate: string;
  lastCompletedOn: string | null;
  safetyRepEmail: string | null;
  status: PlanStatus;
  openDeviations: number;
};

/** «Oversikt og logg»: key figures and how each plan is doing */
export type Oversight = {
  /** HR and the general manager see every plan, a safety representative their own */
  scope: 'all' | 'ownPlans';
  keyFigures: { plans: number; overdue: number; openDeviations: number; completedLastYear: number };
  plans: OversightPlan[];
};

export type LogGroup = 'ALL' | 'PLANS' | 'ROUNDS' | 'DEVIATIONS' | 'TEMPLATES';

export type LogFilter = { group: LogGroup; from: string; to: string };

/** A line of the log as the reader reads it. The server writes the texts, so every export agrees. */
export type LogEntry = {
  id: number;
  at: string;
  type: string;
  label: string;
  planId: number | null;
  plan: string | null;
  actor: string | null;
  summary: string;
};

export type LogPage = { total: number; entries: LogEntry[]; nextBefore: number | null };
export type LogExport = { total: number; truncated: boolean; entries: LogEntry[] };

const logParams = (filter: LogFilter, language: string) => ({
  group: filter.group,
  language,
  ...(filter.from ? { from: filter.from } : {}),
  ...(filter.to ? { to: filter.to } : {}),
});

export const getOversight = () => get<Oversight>('/api/safety-rounds/oversight');
export const getLog = (filter: LogFilter, language: string, before: number | null) =>
  get<LogPage>('/api/safety-rounds/log', { params: { ...logParams(filter, language), ...(before ? { before } : {}) } });
export const getLogExport = (filter: LogFilter, language: string) =>
  get<LogExport>('/api/safety-rounds/log/export', { params: logParams(filter, language) });

const downloadBlob = async (url: string) => {
  const response = await fetch(url, { method: 'GET', credentials: 'include' });
  if (!response.ok) throw new Error('Export failed');
  return await response.blob();
};

export const downloadLogCsv = (filter: LogFilter, language: string) =>
  downloadBlob(`/api/safety-rounds/log/csv?${new URLSearchParams(logParams(filter, language))}`);

export const downloadLogPdf = (filter: LogFilter, language: string) =>
  downloadBlob(
    `/api/pdf-export?${new URLSearchParams({ key: 'safety-round-log', ...logParams(filter, language), download: 'true' })}`,
  );

